import { z } from 'zod';

import {
  entityIdSchema,
  isoDateSchema,
  isoDateTimeSchema,
  pageMetaSchema,
  revisionSchema,
} from './common.js';
import { paymentMethodSchema } from './finance.js';

const idempotencyKeySchema = z.string().trim().min(8).max(200);
const positiveCentsSchema = z.number().int().positive().max(1_000_000_000);
const nonnegativeCentsSchema = z.number().int().nonnegative().max(1_000_000_000);
const reasonSchema = z.string().trim().min(1).max(2_000);

export const financialAdjustmentKindSchema = z.enum(['supplement', 'reduction', 'credit_issue']);

export const financialEventTypes = [
  'payment',
  'supplement',
  'reduction',
  'cash_refund',
  'credit_issue',
  'credit_application',
  'credit_application_release',
  'gift_voucher_application',
  'gift_voucher_release',
] as const;

export type FinancialEventType = (typeof financialEventTypes)[number];

export const financialEventTypeSchema = z.enum(financialEventTypes);

export const financialEventTypeLabels = {
  payment: 'Paiement',
  supplement: 'Supplément',
  reduction: 'Réduction',
  cash_refund: 'Remboursement cash',
  credit_issue: 'Émission d’Avoir',
  credit_application: 'Application d’Avoir',
  credit_application_release: 'Libération d’Avoir',
  gift_voucher_application: 'Application de Bon cadeau',
  gift_voucher_release: 'Libération de Bon cadeau',
} as const satisfies Readonly<Record<FinancialEventType, string>>;

export const periodStateSchema = z.enum(['open', 'locked']);

export const financialSettlementSchema = z
  .object({
    reservationId: entityIdSchema,
    totalCents: nonnegativeCentsSchema,
    grossCashPaidCents: nonnegativeCentsSchema,
    cashRefundedCents: nonnegativeCentsSchema,
    creditIssuedCents: nonnegativeCentsSchema,
    cashPaidCents: nonnegativeCentsSchema,
    creditAppliedCents: nonnegativeCentsSchema,
    giftVoucherAppliedCents: nonnegativeCentsSchema,
    settledCents: nonnegativeCentsSchema,
    balanceDueCents: nonnegativeCentsSchema,
    refundableCashCents: nonnegativeCentsSchema,
  })
  .strict();

export const financialAdjustmentInputSchema = z
  .object({
    kind: financialAdjustmentKindSchema,
    amountCents: positiveCentsSchema,
    accountingDate: isoDateSchema,
    reason: reasonSchema,
    revision: revisionSchema,
    creditExpiresOn: isoDateSchema.nullable().default(null),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.kind === 'credit_issue' &&
      value.creditExpiresOn !== null &&
      value.creditExpiresOn < value.accountingDate
    ) {
      context.addIssue({
        code: 'custom',
        path: ['creditExpiresOn'],
        message: 'L’expiration de l’Avoir précède sa date d’émission.',
      });
    }
    if (value.kind !== 'credit_issue' && value.creditExpiresOn !== null) {
      context.addIssue({
        code: 'custom',
        path: ['creditExpiresOn'],
        message: 'Une expiration ne s’applique qu’à un Avoir.',
      });
    }
  });

export const paymentRefundInputSchema = z
  .object({
    paymentId: entityIdSchema,
    amountCents: positiveCentsSchema,
    accountingDate: isoDateSchema,
    reason: reasonSchema,
    externalReference: z.string().trim().max(200).default(''),
    revision: revisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const financialMutationResultSchema = z
  .object({
    eventId: entityIdSchema,
    reservationId: entityIdSchema,
    creditId: entityIdSchema.nullable(),
    settlement: financialSettlementSchema,
  })
  .strict();

export const financialLedgerEventSchema = z
  .object({
    eventKey: z
      .string()
      .trim()
      .min(3)
      .max(300)
      .regex(/^[^:]+:.+$/u),
    sourceId: entityIdSchema,
    accountingDate: isoDateSchema,
    occurredAt: isoDateTimeSchema,
    type: financialEventTypeSchema,
    reference: z.string().trim().min(1).max(200),
    reservationId: entityIdSchema,
    reservationReference: z.string().trim().min(1).max(160),
    customerId: entityIdSchema,
    customerName: z.string().trim().min(1).max(240),
    stayId: entityIdSchema,
    stayReference: z.string().trim().min(1).max(160),
    signedAmountCents: z.number().int(),
    methodSource: z.string().trim().min(1).max(120),
    externalReference: z.string().trim().max(200),
    actorId: entityIdSchema.nullable(),
    periodState: periodStateSchema,
  })
  .strict();

export const financialEventListQuerySchema = z
  .object({
    cursor: z.string().trim().min(1).max(1_000).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export const financialLedgerEventPageSchema = z
  .object({ items: z.array(financialLedgerEventSchema), page: pageMetaSchema })
  .strict();

export const paymentProviderStatusSchema = z
  .object({
    configured: z.boolean(),
    provider: z.string().trim().min(1).max(80).nullable(),
  })
  .strict();

export const paymentLinkCreateInputSchema = z
  .object({
    amountCents: positiveCentsSchema,
    expiresAt: isoDateTimeSchema,
    returnUrl: z.url(),
    revision: revisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const paymentLinkSchema = z
  .object({
    id: entityIdSchema,
    reservationId: entityIdSchema,
    provider: z.string().trim().min(1).max(80),
    externalId: z.string().trim().min(1).max(200),
    urlHttps: z.url(),
    amountCents: positiveCentsSchema,
    expiresAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    status: z.enum(['active', 'expired', 'used', 'cancelled']),
  })
  .strict();

export const paymentLinkPageSchema = z
  .object({ items: z.array(paymentLinkSchema), page: pageMetaSchema })
  .strict();

export const accountingPeriodSchema = z
  .object({
    id: entityIdSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    status: periodStateSchema,
    revision: revisionSchema,
    lockedBy: entityIdSchema.nullable(),
    lockedAt: isoDateTimeSchema.nullable(),
    unlockedBy: entityIdSchema.nullable(),
    unlockedAt: isoDateTimeSchema.nullable(),
    unlockReason: z.string().max(2_000),
    createdBy: entityIdSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
  })
  .strict();

export const accountingPeriodCreateInputSchema = z
  .object({
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict()
  .refine((value) => value.endsOn >= value.startsOn, {
    path: ['endsOn'],
    message: 'La fin de période précède son début.',
  });

export const accountingPeriodMutationInputSchema = z
  .object({
    revision: revisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const accountingPeriodUnlockInputSchema = accountingPeriodMutationInputSchema
  .extend({ reason: reasonSchema })
  .strict();

export const accountingPeriodListQuerySchema = z
  .object({
    cursor: entityIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    status: periodStateSchema.optional(),
  })
  .strict();

export const accountingPeriodPageSchema = z
  .object({ items: z.array(accountingPeriodSchema), page: pageMetaSchema })
  .strict();

export const accountingExportQuerySchema = z
  .object({
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
    type: financialEventTypeSchema.optional(),
    method: paymentMethodSchema.optional(),
    customerId: entityIdSchema.optional(),
    stayId: entityIdSchema.optional(),
    reservationId: entityIdSchema.optional(),
    periodState: periodStateSchema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.dateFrom === undefined || value.dateTo === undefined || value.dateTo >= value.dateFrom,
    { path: ['dateTo'], message: 'La fin du filtre précède son début.' },
  );

export const accountingExportTotalsSchema = z
  .object({
    rowCount: z.number().int().nonnegative(),
    signedAmountCents: z.number().int(),
  })
  .strict();

export const financialIssuerConfigurationSchema = z
  .object({
    legalName: z.string().trim().min(1).max(240),
    registrationNumber: z.string().trim().min(1).max(120),
    address: z.string().trim().min(1).max(500),
    email: z.email(),
    currency: z.enum(['EUR']),
    revision: revisionSchema,
    updatedBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
  })
  .strict();

export const financialIssuerConfigurationStateSchema = z
  .object({
    configured: z.boolean(),
    value: financialIssuerConfigurationSchema.nullable(),
  })
  .strict();

export const financialIssuerConfigurationInputSchema = z
  .object({
    legalName: z.string().trim().min(1).max(240),
    registrationNumber: z.string().trim().min(1).max(120),
    address: z.string().trim().min(1).max(500),
    email: z.email(),
    currency: z.literal('EUR').default('EUR'),
    revision: z.number().int().nonnegative(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const financialDocumentKindSchema = z.enum(['invoice', 'statement']);

const financialDocumentLineSchema = z
  .object({
    label: z.string().trim().min(1).max(500),
    sourceReference: z.string().trim().min(1).max(128).nullable().default(null),
    quantity: z.number().int().positive(),
    unitPriceCents: nonnegativeCentsSchema,
    lineTotalCents: nonnegativeCentsSchema,
  })
  .strict();

const financialDocumentSettlementLineSchema = z
  .object({
    eventKey: z
      .string()
      .trim()
      .min(3)
      .max(300)
      .regex(/^[^:]+:.+$/u),
    sourceId: entityIdSchema,
    accountingDate: isoDateSchema,
    type: financialEventTypeSchema,
    signedAmountCents: z.number().int(),
    methodSource: z.string().trim().min(1).max(120),
    externalReference: z.string().trim().max(200),
  })
  .strict();

export const financialDocumentSnapshotSchema = z
  .object({
    schemaVersion: z.literal(1),
    documentNumber: z.string().regex(/^(?:FAC|REL)-\d{4}-[A-F0-9]{12}$/u),
    kind: financialDocumentKindSchema,
    issuedAt: isoDateTimeSchema,
    issuer: financialIssuerConfigurationSchema.pick({
      legalName: true,
      registrationNumber: true,
      address: true,
      email: true,
      currency: true,
      revision: true,
    }),
    reservation: z
      .object({
        id: entityIdSchema,
        reference: z.string().trim().min(1).max(160),
        revision: revisionSchema,
      })
      .strict(),
    customer: z
      .object({
        id: entityIdSchema,
        name: z.string().trim().min(1).max(240),
      })
      .strict(),
    stay: z
      .object({
        id: entityIdSchema,
        reference: z.string().trim().min(1).max(160),
      })
      .strict(),
    lines: z.array(financialDocumentLineSchema).max(500),
    settlements: z.array(financialDocumentSettlementLineSchema),
    totals: financialSettlementSchema.omit({ reservationId: true }),
    periodState: periodStateSchema,
    disclaimer: z.literal(
      'Document financier interne — aucune conformité fiscale n’est affirmée par Canopée.',
    ),
  })
  .strict();

export const financialDocumentGenerateInputSchema = z
  .object({
    kind: financialDocumentKindSchema,
    reservationRevision: revisionSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const financialDocumentSchema = z
  .object({
    id: entityIdSchema,
    documentNumber: z.string().regex(/^(?:FAC|REL)-\d{4}-[A-F0-9]{12}$/u),
    kind: financialDocumentKindSchema,
    reservationId: entityIdSchema,
    customerId: entityIdSchema,
    issuedAt: isoDateTimeSchema,
    version: z.literal(1),
    filename: z.string().trim().min(1).max(180),
    sha256: z.string().regex(/^[a-f0-9]{64}$/u),
    sizeBytes: z.number().int().positive(),
  })
  .strict();

export const financialDocumentPageSchema = z
  .object({ items: z.array(financialDocumentSchema), page: pageMetaSchema })
  .strict();

export type AccountingExportQuery = z.infer<typeof accountingExportQuerySchema>;
export type AccountingPeriod = z.infer<typeof accountingPeriodSchema>;
export type AccountingPeriodPage = z.infer<typeof accountingPeriodPageSchema>;
export type AccountingPeriodCreateInput = z.infer<typeof accountingPeriodCreateInputSchema>;
export type AccountingPeriodListQuery = z.infer<typeof accountingPeriodListQuerySchema>;
export type AccountingPeriodMutationInput = z.infer<typeof accountingPeriodMutationInputSchema>;
export type AccountingPeriodUnlockInput = z.infer<typeof accountingPeriodUnlockInputSchema>;
export type FinancialAdjustmentInput = z.infer<typeof financialAdjustmentInputSchema>;
export type FinancialDocument = z.infer<typeof financialDocumentSchema>;
export type FinancialDocumentPage = z.infer<typeof financialDocumentPageSchema>;
export type FinancialDocumentGenerateInput = z.infer<typeof financialDocumentGenerateInputSchema>;
export type FinancialDocumentKind = z.infer<typeof financialDocumentKindSchema>;
export type FinancialDocumentSnapshot = z.infer<typeof financialDocumentSnapshotSchema>;
export type FinancialEventListQuery = z.infer<typeof financialEventListQuerySchema>;
export type FinancialLedgerEvent = z.infer<typeof financialLedgerEventSchema>;
export type FinancialLedgerEventPage = z.infer<typeof financialLedgerEventPageSchema>;
export type FinancialMutationResult = z.infer<typeof financialMutationResultSchema>;
export type FinancialSettlement = z.infer<typeof financialSettlementSchema>;
export type FinancialIssuerConfiguration = z.infer<typeof financialIssuerConfigurationSchema>;
export type FinancialIssuerConfigurationInput = z.infer<
  typeof financialIssuerConfigurationInputSchema
>;
export type PaymentLink = z.infer<typeof paymentLinkSchema>;
export type PaymentLinkPage = z.infer<typeof paymentLinkPageSchema>;
export type PaymentLinkCreateInput = z.infer<typeof paymentLinkCreateInputSchema>;
export type PaymentRefundInput = z.infer<typeof paymentRefundInputSchema>;
export type PaymentProviderStatus = z.infer<typeof paymentProviderStatusSchema>;
